import React, { useState, useEffect, useCallback, useRef } from 'react';
import { updatesAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import ConfirmModal from '../components/ConfirmModal';
import Toast from '../components/Toast';
import './Updates.css';

// Update type configurations with icons
const UPDATE_TYPES = {
  announcement: { label: 'Announcement', icon: 'megaphone', color: '#3B82F6' },
  new_feature: { label: 'New Feature', icon: 'sparkles', color: '#8B5CF6' },
  maintenance: { label: 'Maintenance', icon: 'wrench', color: '#F59E0B' },
  warning: { label: 'Warning', icon: 'warning', color: '#EF4444' },
  offer: { label: 'Offer', icon: 'gift', color: '#10B981' },
  info: { label: 'Info', icon: 'info', color: '#06B6D4' },
  update: { label: 'Update', icon: 'refresh', color: '#6366F1' }
};

// SVG Icons
const Icons = {
  megaphone: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 11 18-5v12L3 13v-2z"></path>
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path>
    </svg>
  ),
  sparkles: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"></path>
      <path d="M5 3v4"></path>
      <path d="M19 17v4"></path>
      <path d="M3 5h4"></path>
      <path d="M17 19h4"></path>
    </svg>
  ),
  wrench: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
    </svg>
  ),
  warning: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
      <path d="M12 9v4"></path>
      <path d="M12 17h.01"></path>
    </svg>
  ),
  gift: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="4" rx="1"></rect>
      <path d="M12 8v13"></path>
      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"></path>
      <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"></path>
    </svg>
  ),
  info: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <path d="M12 16v-4"></path>
      <path d="M12 8h.01"></path>
    </svg>
  ),
  refresh: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
      <path d="M21 3v5h-5"></path>
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
      <path d="M8 16H3v5"></path>
    </svg>
  )
};

const Updates = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [formData, setFormData] = useState({
    type: 'announcement',
    title: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });

  // Toast
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Dropdown refs
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);

  const fetchUpdates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await updatesAPI.getAll(filters);
      if (response.data.success) {
        setUpdates(response.data.data.updates);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch updates:', error);
      showToast('Failed to fetch updates', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = async () => {
    try {
      const response = await updatesAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchUpdates();
  }, [fetchUpdates]);

  useEffect(() => {
    fetchStats();
  }, []);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
        setTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const handleStatusFilter = (status) => {
    setFilters(prev => ({ ...prev, status: prev.status === status ? '' : status, page: 1 }));
    setStatusDropdownOpen(false);
  };

  const handleTypeFilter = (type) => {
    setFilters(prev => ({ ...prev, type: prev.type === type ? '' : type, page: 1 }));
    setTypeDropdownOpen(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  const openCreateModal = () => {
    setModalMode('create');
    setEditingUpdate(null);
    setFormData({ type: 'announcement', title: '', description: '' });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (update) => {
    setModalMode('edit');
    setEditingUpdate(update);
    setFormData({
      type: update.type,
      title: update.title,
      description: update.description
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUpdate(null);
    setFormData({ type: 'announcement', title: '', description: '' });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.length > 255) {
      errors.title = 'Title must be 255 characters or less';
    }
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (modalMode === 'create') {
        await updatesAPI.create(formData);
        showToast('Update created successfully');
      } else {
        await updatesAPI.update(editingUpdate.id, formData);
        showToast('Update modified successfully');
      }
      closeModal();
      fetchUpdates();
      fetchStats();
    } catch (error) {
      console.error('Failed to save update:', error);
      showToast(error.response?.data?.message || 'Failed to save update', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await updatesAPI.toggle(id);
      if (response.data.success) {
        showToast(response.data.message);
        fetchUpdates();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to toggle status:', error);
      showToast('Failed to toggle status', 'error');
    }
  };

  const handleDelete = (update) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Update',
      message: `Are you sure you want to delete "${update.title}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await updatesAPI.delete(update.id);
          showToast('Update deleted successfully');
          fetchUpdates();
          fetchStats();
        } catch (error) {
          console.error('Failed to delete update:', error);
          showToast('Failed to delete update', 'error');
        }
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  const getTypeIcon = (type) => {
    const config = UPDATE_TYPES[type];
    if (!config) return null;
    return (
      <span className="type-icon" style={{ backgroundColor: `${config.color}15`, color: config.color }}>
        {Icons[config.icon]}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const statusFilterOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const typeFilterOptions = [
    { value: '', label: 'All Types' },
    ...Object.entries(UPDATE_TYPES).map(([value, config]) => ({
      value,
      label: config.label
    }))
  ];

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">PLATFORM UPDATES</h1>
            <p className="page-subtitle">MANAGE ANNOUNCEMENTS & NOTIFICATIONS</p>
          </div>
          <div className="header-right">
            {/* Status Filter */}
            <div className="custom-dropdown" ref={statusDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
              >
                <span>{statusFilterOptions.find(opt => opt.value === filters.status)?.label || 'All Status'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {statusDropdownOpen && (
                <div className="custom-dropdown-menu">
                  {statusFilterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`custom-dropdown-item ${filters.status === option.value ? 'active' : ''}`}
                      onClick={() => handleStatusFilter(option.value)}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Type Filter */}
            <div className="custom-dropdown" ref={typeDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
              >
                <span>{typeFilterOptions.find(opt => opt.value === filters.type)?.label || 'All Types'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {typeDropdownOpen && (
                <div className="custom-dropdown-menu">
                  {typeFilterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`custom-dropdown-item ${filters.type === option.value ? 'active' : ''}`}
                      onClick={() => handleTypeFilter(option.value)}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button className="btn-primary" onClick={openCreateModal}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              ADD UPDATE
            </button>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Stats Cards */}
          <div className="stats-grid stats-grid-3">
            <div
              className={`stat-card stat-card-clickable ${filters.status === '' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('')}
            >
              <div className="stat-info">
                <span className="stat-label">TOTAL UPDATES</span>
                <span className="stat-value">{stats.total}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-success stat-card-clickable ${filters.status === 'active' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('active')}
            >
              <div className="stat-info">
                <span className="stat-label">ACTIVE</span>
                <span className="stat-value">{stats.active}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-card-clickable ${filters.status === 'inactive' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('inactive')}
            >
              <div className="stat-info">
                <span className="stat-label">INACTIVE</span>
                <span className="stat-value">{stats.inactive}</span>
              </div>
            </div>
          </div>

          {/* Updates Table */}
          <div className="dashboard-section updates-table-section">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading updates...</p>
              </div>
            ) : updates.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="m3 11 18-5v12L3 13v-2z"></path>
                  <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path>
                </svg>
                <p>No updates found</p>
                <button className="btn-primary" onClick={openCreateModal}>
                  Create First Update
                </button>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>TYPE</th>
                        <th>TITLE</th>
                        <th>DESCRIPTION</th>
                        <th>STATUS</th>
                        <th>CREATED</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {updates.map((update) => (
                        <tr key={update.id}>
                          <td>
                            <div className="type-cell">
                              {getTypeIcon(update.type)}
                              <span className="type-label">{UPDATE_TYPES[update.type]?.label}</span>
                            </div>
                          </td>
                          <td>
                            <span className="update-title">{update.title}</span>
                          </td>
                          <td>
                            <span className="update-description">{update.description}</span>
                          </td>
                          <td>
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={update.is_active}
                                onChange={() => handleToggle(update.id)}
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </td>
                          <td>
                            <span className="date-text">{formatDate(update.created_at)}</span>
                          </td>
                          <td>
                            <div className="actions-cell">
                              <button
                                className="btn-action btn-edit"
                                onClick={() => openEditModal(update)}
                              >
                                EDIT
                              </button>
                              <button
                                className="btn-action btn-delete"
                                onClick={() => handleDelete(update)}
                              >
                                DELETE
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="table-footer">
                    <div className="pagination-info">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                      {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                      {pagination.total} updates
                    </div>
                    <div className="pagination-controls">
                      <button
                        className="pagination-btn"
                        disabled={pagination.page === 1}
                        onClick={() => handlePageChange(pagination.page - 1)}
                      >
                        Previous
                      </button>
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        className="pagination-btn"
                        disabled={pagination.page === pagination.totalPages}
                        onClick={() => handlePageChange(pagination.page + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content update-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {modalMode === 'create' ? 'Create Update' : 'Edit Update'}
              </h2>
              <button className="modal-close" onClick={closeModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Type Selection */}
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <div className="type-selector">
                    {Object.entries(UPDATE_TYPES).map(([value, config]) => (
                      <button
                        key={value}
                        type="button"
                        className={`type-option ${formData.type === value ? 'active' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, type: value }))}
                        style={{
                          '--type-color': config.color,
                          '--type-bg': `${config.color}15`
                        }}
                      >
                        {Icons[config.icon]}
                        <span>{config.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div className="form-group">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    className={`form-input ${formErrors.title ? 'error' : ''}`}
                    placeholder="Enter update title..."
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    maxLength={255}
                  />
                  {formErrors.title && <span className="form-error">{formErrors.title}</span>}
                  <span className="char-count">{formData.title.length}/255</span>
                </div>

                {/* Description */}
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className={`form-textarea ${formErrors.description ? 'error' : ''}`}
                    placeholder="Enter update description..."
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                  />
                  {formErrors.description && <span className="form-error">{formErrors.description}</span>}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : (modalMode === 'create' ? 'Create Update' : 'Save Changes')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
      />

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}
    </div>
  );
};

export default Updates;
